'use client';

import {
    type CSSProperties,
    type ReactNode,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { cn } from '@/lib/cn';
import {
    MARKER_SHAPES,
    MARKER_STAGGER_MS,
    type MarkerTone,
    type MarkerVariant,
    marker,
} from './variants';

export type { MarkerTone, MarkerVariant };

export interface MarkerProps {
    children?: ReactNode;
    variant?: MarkerVariant;
    /** Ink colour; `auto` picks the style's own tone. */
    tone?: MarkerTone;
    /** Draw in once scrolled into view; off renders the mark finished. */
    animate?: boolean;
    /** Controlled: whether the mark is drawn. Omit to draw on first view. */
    drawn?: boolean;
    /** One stroke's draw time. */
    durationMs?: number;
    delayMs?: number;
    className?: string;
    /** Optional editor ink colour or CSS gradient. */
    color?: string;
    gradient?: string;
    angle?: number;
    /** Editor size multiplier. */
    size?: number;
    thickness?: number;
    dotSize?: number;
    radius?: number;
    offsetY?: number;
    replayKey?: string;
    style?: CSSProperties;
    customSvg?: string;
    customImage?: string;
    layer?: 'over' | 'under';
    direction?: 'center' | 'left';
}

type MarkerLineRect = {
    left: number;
    top: number;
    width: number;
    height: number;
};

function mergeRectsOnVisualLines(rects: MarkerLineRect[]) {
    const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left);

    return sorted.reduce<MarkerLineRect[]>((lines, rect) => {
        const line = lines[lines.length - 1];
        const sharesLine =
            line &&
            Math.min(line.top + line.height, rect.top + rect.height) -
                Math.max(line.top, rect.top) >
                Math.min(line.height, rect.height) * 0.5;

        if (!sharesLine) {
            lines.push({ ...rect });
            return lines;
        }

        const right = Math.max(line.left + line.width, rect.left + rect.width);
        const bottom = Math.max(line.top + line.height, rect.top + rect.height);
        line.left = Math.min(line.left, rect.left);
        line.top = Math.min(line.top, rect.top);
        line.width = right - line.left;
        line.height = bottom - line.top;
        return lines;
    }, []);
}

const NON_PAINT_VALUES = new Set([
    'none',
    'transparent',
    'inherit',
    'unset',
    'initial',
]);

// Baby UI's single-line circle has an intentionally open, elliptical gesture.
// A flatter companion path keeps every corner of a wrapped text block inside
// the mark without making the entire circle excessively wider than its text.
const MULTILINE_CIRCLE_STROKES = [
    {
        d: 'M14,8 C58,2 162,2 206,8 C214,10 218,18 218,32 C218,46 214,54 206,56 C162,62 58,62 14,56 C6,54 2,46 2,32 C2,18 6,10 14,8 Z',
        width: 3,
        rough: 'hard' as const,
    },
    {
        d: 'M16,11 C62,5 158,5 204,11 C210,14 213,21 213,32 C213,43 210,50 204,53 C158,59 62,59 16,53 C10,50 7,43 7,32 C7,21 10,14 16,11 Z',
        width: 1.5,
        rough: 'soft' as const,
        opacity: 0.55,
        second: true,
    },
];

const WAVY_VIEWBOX_HEIGHT = 14;

/**
 * Build as many full wave cycles as the rendered fragment needs. The path is
 * intentionally allowed to extend past the viewBox so the final cycle is
 * clipped instead of squeezed to fit a short wrapped line.
 */
function createWavyPath(width: number, wavelength: number) {
    const centerY = WAVY_VIEWBOX_HEIGHT / 2;
    const amplitude = WAVY_VIEWBOX_HEIGHT * 0.22;
    const cycle = Math.max(wavelength, 1);
    const cycles = Math.max(1, Math.ceil(width / cycle));
    let path = `M0,${centerY}`;

    for (let index = 0; index < cycles; index += 1) {
        const start = index * cycle;
        const quarter = cycle / 4;
        const half = cycle / 2;
        path += ` C${start + quarter * 0.55},${centerY - amplitude} ${
            start + quarter * 1.45
        },${centerY - amplitude} ${start + half},${centerY}`;
        path += ` C${start + half + quarter * 0.55},${centerY + amplitude} ${
            start + half + quarter * 1.45
        },${centerY + amplitude} ${start + cycle},${centerY}`;
    }

    return path;
}

function shouldKeepSvgPaint(value: string) {
    return NON_PAINT_VALUES.has(value.trim().toLowerCase());
}

/**
 * Makes pasted SVG artwork follow the editor swatches. Existing definitions
 * are kept intact for masks and filters, while visible fill/stroke paint is
 * redirected to an editor-owned gradient.
 */
function recolorCustomSvg(
    source: string,
    colors: string[],
    fallbackColor: string,
    angle: number,
    gradientId: string
) {
    const svg = source.trim();
    if (!/^<svg\b/i.test(svg)) return svg;

    const palette = colors.length ? colors : [fallbackColor || 'currentColor'];
    const stops = (palette.length === 1 ? [palette[0], palette[0]] : palette)
        .map(
            (stopColor, index, items) =>
                `<stop offset="${
                    (index / (items.length - 1)) * 100
                }%" stop-color="${stopColor}"/>`
        )
        .join('');
    const editorGradient = `<defs data-editor-marker-ink="true"><linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(${angle} .5 .5)">${stops}</linearGradient></defs>`;
    const paint = `url(#${gradientId})`;
    const definitions: string[] = [];

    let recolored = svg.replace(/<defs\b[\s\S]*?<\/defs\s*>/gi, (block) => {
        const placeholder = `__EDITOR_MARKER_DEFS_${definitions.length}__`;
        definitions.push(block);
        return placeholder;
    });

    recolored = recolored.replace(
        /\s(fill|stroke)\s*=\s*(["'])(.*?)\2/gi,
        (attribute, name, quote, value) =>
            shouldKeepSvgPaint(value)
                ? attribute
                : ` ${name}=${quote}${paint}${quote}`
    );
    recolored = recolored.replace(
        /\s(fill|stroke)\s*=\s*([^"'\s>]+)/gi,
        (attribute, name, value) =>
            shouldKeepSvgPaint(value) ? attribute : ` ${name}="${paint}"`
    );
    recolored = recolored.replace(
        /\sstyle\s*=\s*(["'])(.*?)\1/gi,
        (attribute, quote, declarations) => {
            const nextDeclarations = declarations.replace(
                /((?:^|;)\s*)(fill|stroke)\s*:\s*([^;]+)/gi,
                (declaration, prefix, property, value) =>
                    shouldKeepSvgPaint(value)
                        ? declaration
                        : `${prefix}${property}: ${paint}`
            );
            return ` style=${quote}${nextDeclarations}${quote}`;
        }
    );

    recolored = recolored.replace(
        /__EDITOR_MARKER_DEFS_(\d+)__/g,
        (_, index) => definitions[Number(index)] || ''
    );
    recolored = recolored.replace(
        /<style\b([^>]*)>([\s\S]*?)<\/style\s*>/gi,
        (styleBlock, attributes, css) => {
            const nextCss = css.replace(
                /((?:^|[;{])\s*)(fill|stroke)\s*:\s*([^;}]+)/gi,
                (declaration, prefix, property, value) =>
                    shouldKeepSvgPaint(value)
                        ? declaration
                        : `${prefix}${property}: ${paint}`
            );
            return `<style${attributes}>${nextCss}</style>`;
        }
    );

    return recolored.replace(/<svg\b([^>]*)>/i, (opening, attributes) => {
        const hasDefaultFill = /\sfill\s*=/i.test(attributes);
        return `<svg${attributes}${
            hasDefaultFill ? '' : ` fill="${paint}"`
        }>${editorGradient}`;
    });
}

export function Marker({
    children,
    variant = 'wavy',
    tone = 'auto',
    animate = true,
    drawn: drawnProp,
    durationMs = 700,
    delayMs = 0,
    className,
    color,
    gradient,
    angle,
    size = 1,
    thickness = 2,
    dotSize = 1.5,
    radius = 32,
    offsetY = 0,
    replayKey,
    style: externalStyle,
    customSvg,
    customImage,
    layer,
    direction = 'left',
}: MarkerProps) {
    const id = useId().replace(/:/g, '');
    const rootRef = useRef<HTMLSpanElement>(null);
    const contentRef = useRef<HTMLSpanElement>(null);
    const originRef = useRef<HTMLSpanElement>(null);
    const animationTimeoutRef = useRef<number>();
    const animationRunRef = useRef({ key: '', started: false });
    const [lineRects, setLineRects] = useState<MarkerLineRect[]>([]);
    const [isGroupedOverall, setIsGroupedOverall] = useState(false);
    const [endsOverallGroup, setEndsOverallGroup] = useState(true);
    const [seen, setSeen] = useState(false);
    const [animationSettled, setAnimationSettled] = useState(!animate);
    const drawn = drawnProp ?? seen;
    const styles = marker({ variant, tone });
    const shape = MARKER_SHAPES[variant];
    const usesOverallShape = variant === 'circle' || variant === 'box';
    const usesSequentialLines = [
        'line',
        'wavy',
        'highlight',
        'underline',
        'double-underline',
        'strikethrough',
        'cross-out',
        'arrow',
        'custom',
    ].includes(variant);
    const lineSequenceDuration =
        durationMs * (shape.kind === 'fill' ? 1.1 : 1) +
        (shape.kind === 'strokes' &&
        shape.strokes.some((stroke) => stroke.second)
            ? MARKER_STAGGER_MS
            : 0);
    const resolvedLayer =
        layer ??
        (variant === 'strikethrough' || variant === 'cross-out'
            ? 'over'
            : 'under');

    useEffect(() => {
        if (!animate || drawnProp !== undefined) return;
        setSeen(false);
        const node = rootRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry?.isIntersecting) return;
            setSeen(true);
            observer.disconnect();
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [animate, drawnProp, replayKey]);

    useEffect(() => {
        const runKey = `${replayKey ?? ''}:${animate ? 'animated' : 'static'}`;
        const clearAnimationTimeout = () => {
            if (animationTimeoutRef.current !== undefined) {
                window.clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = undefined;
            }
        };

        if (!animate) {
            clearAnimationTimeout();
            animationRunRef.current = { key: runKey, started: false };
            setAnimationSettled(true);
            return;
        }

        if (!drawn) {
            clearAnimationTimeout();
            animationRunRef.current = { key: runKey, started: false };
            setAnimationSettled(false);
            return;
        }

        // Wait until the browser has measured the wrapped fragments. This
        // ensures the first run is timed for every visual line, while later
        // resizes only update geometry and never replay a finished effect.
        if (lineRects.length === 0) return;
        if (
            animationRunRef.current.key === runKey &&
            animationRunRef.current.started
        ) {
            return;
        }

        clearAnimationTimeout();
        animationRunRef.current = { key: runKey, started: true };
        setAnimationSettled(false);
        animationTimeoutRef.current = window.setTimeout(
            () => {
                animationTimeoutRef.current = undefined;
                setAnimationSettled(true);
            },
            delayMs +
                lineSequenceDuration *
                    Math.max(1, usesSequentialLines ? lineRects.length : 1) +
                100
        );
    }, [
        animate,
        delayMs,
        drawn,
        lineRects.length,
        lineSequenceDuration,
        replayKey,
        usesSequentialLines,
    ]);

    useEffect(
        () => () => {
            if (animationTimeoutRef.current !== undefined) {
                window.clearTimeout(animationTimeoutRef.current);
            }
        },
        []
    );

    const measureLines = useCallback(() => {
        const content = contentRef.current;
        const origin = originRef.current;
        const root = rootRef.current;
        if (!content || !origin || !root) return;

        const originRect = origin.getBoundingClientRect();
        let measuredContents = [content];
        let joinsStyledFragments = false;

        if ((usesOverallShape || usesSequentialLines) && replayKey) {
            const groupScope =
                root.closest('[data-markdown-field-key]') ||
                root.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote') ||
                root.parentElement;
            const groupMembers = Array.from(
                groupScope?.querySelectorAll<HTMLElement>(
                    '[data-marker-group]'
                ) || []
            ).filter(
                (member) =>
                    member.dataset.markerGroup === String(replayKey) &&
                    member.dataset.variant === variant
            );

            if (groupMembers.length > 1) {
                joinsStyledFragments = true;
                setIsGroupedOverall(usesOverallShape);
                setEndsOverallGroup(
                    usesOverallShape
                        ? groupMembers[groupMembers.length - 1] === root
                        : true
                );

                // A previous partial edit may leave several adjacent styled
                // spans. Only the first span owns the shared decoration so a
                // later Apply renders once per visual line, not once per old
                // text-node boundary.
                if (groupMembers[0] !== root) {
                    setLineRects([]);
                    return;
                }

                measuredContents = groupMembers
                    .map((member) =>
                        Array.from(member.children).find((child) =>
                            child.classList.contains('editor-marker-content')
                        )
                    )
                    .filter(Boolean) as HTMLSpanElement[];
            } else {
                setIsGroupedOverall(false);
                setEndsOverallGroup(true);
            }
        } else {
            setIsGroupedOverall(false);
            setEndsOverallGroup(true);
        }

        const measuredRects = measuredContents
            .flatMap((measuredContent) =>
                Array.from(measuredContent.getClientRects())
            )
            .filter((rect) => rect.width > 0 && rect.height > 0)
            .map((rect) => ({
                left: rect.left - originRect.left,
                top: rect.top - originRect.top,
                width: rect.width,
                height: rect.height,
            }));
        const next = joinsStyledFragments
            ? mergeRectsOnVisualLines(measuredRects)
            : measuredRects;

        setLineRects((current) => {
            const unchanged =
                current.length === next.length &&
                current.every((rect, index) => {
                    const candidate = next[index];
                    return (
                        Math.abs(rect.left - candidate.left) < 0.25 &&
                        Math.abs(rect.top - candidate.top) < 0.25 &&
                        Math.abs(rect.width - candidate.width) < 0.25 &&
                        Math.abs(rect.height - candidate.height) < 0.25
                    );
                });
            return unchanged ? current : next;
        });
    }, [replayKey, usesOverallShape, usesSequentialLines, variant]);

    useLayoutEffect(() => {
        const content = contentRef.current;
        const root = rootRef.current;
        if (!content || !root) return undefined;

        let frame = window.requestAnimationFrame(measureLines);
        const scheduleMeasure = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(measureLines);
        };
        const observer = new ResizeObserver(scheduleMeasure);
        observer.observe(root.parentElement || root);
        window.addEventListener('resize', scheduleMeasure);
        document.fonts?.ready.then(scheduleMeasure).catch(() => {});

        return () => {
            window.cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
        };
    }, [children, measureLines]);

    const decorationRects = (() => {
        if (lineRects.length <= 1) return lineRects;

        const top = Math.min(...lineRects.map((rect) => rect.top));
        const bottom = Math.max(
            ...lineRects.map((rect) => rect.top + rect.height)
        );
        const left = Math.min(...lineRects.map((rect) => rect.left));
        const right = Math.max(
            ...lineRects.map((rect) => rect.left + rect.width)
        );

        if (usesOverallShape) {
            return [
                {
                    left,
                    top,
                    width: right - left,
                    height: bottom - top,
                    overall: true,
                },
            ];
        }

        if (variant === 'bracket') {
            const first = lineRects[0];
            const last = lineRects[lineRects.length - 1];
            return [
                {
                    ...first,
                    bracketSide: 'left' as const,
                },
                {
                    ...last,
                    bracketSide: 'right' as const,
                },
            ];
        }

        return lineRects;
    })();

    const timing = (extra = 0, scale = 1, lineIndex = 0) =>
        ({
            '--marker-duration': `${durationMs * scale}ms`,
            '--marker-delay': `${
                delayMs +
                extra +
                (usesSequentialLines ? lineIndex * lineSequenceDuration : 0)
            }ms`,
        }) as CSSProperties;
    const flag = drawn ? '' : undefined;
    const animateDecoration = animate && !animationSettled;
    const inkId = `${id}-ink`;
    const rootStyle = {
        ...externalStyle,
        '--marker-size': size,
        '--marker-thickness': `${thickness}px`,
        '--marker-dot-size': `${dotSize}px`,
        '--marker-radius': `${radius}px`,
        '--marker-offset-y': `${offsetY}px`,
        ...(gradient ? { '--marker-gradient': gradient } : {}),
    } as CSSProperties;
    const decorationColor = color ? { color } : {};
    const gradientColors =
        gradient?.match(/(?:rgba?\([^)]*\)|#[0-9a-f]{3,8})/gi) ?? [];
    const gradientAngle = Number.isFinite(angle)
        ? Number(angle)
        : Number(gradient?.match(/(-?\d+(?:\.\d+)?)deg/i)?.[1] ?? 90);
    const gradientStart = gradientColors[0] || color || 'currentColor';
    const gradientEnd = gradientColors[1] || gradientStart;
    const svgGradientColors = gradientColors.length
        ? gradientColors
        : [color || 'currentColor', color || 'currentColor'];
    const bracketGradientColors = gradientColors.length
        ? gradientColors
        : [color || 'currentColor', color || 'currentColor'];
    const bracketLeftInkId = `${id}-bracket-left-ink`;
    const bracketRightInkId = `${id}-bracket-right-ink`;
    const customInkId = `${id}-custom-ink`;
    const recoloredCustomSvg = customSvg
        ? recolorCustomSvg(
              customSvg,
              gradientColors,
              color || 'currentColor',
              gradientAngle,
              customInkId
          )
        : '';

    return (
        <>
            <span
                ref={rootRef}
                data-slot="marker"
                data-variant={variant}
                data-marker-group={replayKey || undefined}
                data-marker-layer={resolvedLayer}
                data-highlight-direction={direction}
                className={cn(styles.root(), className)}
                style={rootStyle}
            >
                <span
                    ref={contentRef}
                    className={cn(styles.content(), 'editor-marker-content')}
                >
                    {children}
                </span>
                <span
                    ref={originRef}
                    aria-hidden="true"
                    className="editor-marker-line-origin"
                />
                {decorationRects.map((lineRect, lineIndex) => {
                    const isWavyStroke =
                        variant === 'wavy' && shape.kind === 'strokes';
                    const wavyViewBoxWidth = Math.max(
                        1,
                        (lineRect.width * WAVY_VIEWBOX_HEIGHT) /
                            Math.max(lineRect.height * 0.7, 1)
                    );
                    const wavyWavelength = Math.max(
                        9,
                        (lineRect.height * 0.62 * WAVY_VIEWBOX_HEIGHT) /
                            Math.max(lineRect.height * 0.7, 1)
                    );
                    const renderedViewBox = isWavyStroke
                        ? `0 0 ${wavyViewBoxWidth} ${WAVY_VIEWBOX_HEIGHT}`
                        : shape.kind === 'bar'
                        ? '0 0 1 1'
                        : shape.viewBox;
                    const renderedViewBoxValues = renderedViewBox
                        .split(/\s+/)
                        .map(Number);
                    const renderedGradientWidth = renderedViewBoxValues[2] || 1;
                    const renderedGradientHeight =
                        renderedViewBoxValues[3] || 1;
                    const renderedInkId = `${inkId}-${lineIndex}`;
                    const renderedInk = gradient
                        ? `url(#${renderedInkId})`
                        : 'currentColor';
                    const renderedStrokes =
                        shape.kind === 'strokes'
                            ? isWavyStroke
                                ? [
                                      {
                                          ...shape.strokes[0],
                                          d: createWavyPath(
                                              wavyViewBoxWidth,
                                              wavyWavelength
                                          ),
                                      },
                                  ]
                                : variant === 'circle' && lineRect.overall
                                ? MULTILINE_CIRCLE_STROKES
                                : shape.strokes
                            : [];

                    return (
                        <span
                            key={lineIndex}
                            aria-hidden="true"
                            data-variant={variant}
                            data-marker-layer={resolvedLayer}
                            data-highlight-direction={direction}
                            data-marker-overall={lineRect.overall || undefined}
                            data-marker-bracket-side={lineRect.bracketSide}
                            className={cn(
                                styles.root(),
                                'editor-marker-adapter editor-marker-line-fragment'
                            )}
                            style={
                                {
                                    left: lineRect.left,
                                    top: lineRect.top,
                                    width: lineRect.width,
                                    height: lineRect.height,
                                } as CSSProperties
                            }
                        >
                            {variant === 'custom' &&
                            (customSvg || customImage) ? (
                                <span
                                    aria-hidden="true"
                                    data-drawn={flag}
                                    className={cn(
                                        'editor-marker-custom-decoration editor-marker-decoration',
                                        animateDecoration && 'marker-sweep'
                                    )}
                                    style={{
                                        ...timing(0, 1, lineIndex),
                                        ...decorationColor,
                                    }}
                                >
                                    {customSvg ? (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: recoloredCustomSvg,
                                            }}
                                        />
                                    ) : (
                                        <img src={customImage} alt="" />
                                    )}
                                </span>
                            ) : variant === 'bracket' &&
                              shape.kind === 'strokes' ? (
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        styles.decoration(),
                                        'editor-marker-decoration',
                                        'editor-marker-bracket-decoration'
                                    )}
                                    style={decorationColor}
                                >
                                    {lineRect.bracketSide !== 'right' ? (
                                        <svg
                                            className="editor-marker-bracket-side editor-marker-bracket-left"
                                            viewBox="0 0 30 60"
                                            preserveAspectRatio="none"
                                        >
                                            {gradient ? (
                                                <defs>
                                                    <linearGradient
                                                        id={bracketLeftInkId}
                                                        x1="0%"
                                                        y1="0%"
                                                        x2="100%"
                                                        y2="0%"
                                                        gradientTransform={`rotate(${gradientAngle} .5 .5)`}
                                                    >
                                                        {bracketGradientColors.map(
                                                            (
                                                                stopColor,
                                                                index
                                                            ) => (
                                                                <stop
                                                                    key={`${stopColor}-${index}`}
                                                                    offset={`${
                                                                        (index /
                                                                            Math.max(
                                                                                bracketGradientColors.length -
                                                                                    1,
                                                                                1
                                                                            )) *
                                                                        100
                                                                    }%`}
                                                                    stopColor={
                                                                        stopColor
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </linearGradient>
                                                </defs>
                                            ) : null}
                                            <path
                                                d={shape.strokes[0].d}
                                                pathLength={1}
                                                fill="none"
                                                stroke={
                                                    gradient
                                                        ? `url(#${bracketLeftInkId})`
                                                        : color || gradientStart
                                                }
                                                strokeWidth={
                                                    shape.strokes[0].width
                                                }
                                                strokeLinecap="round"
                                                data-drawn={flag}
                                                className={
                                                    animateDecoration
                                                        ? 'marker-stroke'
                                                        : undefined
                                                }
                                                style={timing(0, 1, lineIndex)}
                                            />
                                        </svg>
                                    ) : null}
                                    {lineRect.bracketSide !== 'left' ? (
                                        <svg
                                            className="editor-marker-bracket-side editor-marker-bracket-right"
                                            viewBox="0 0 30 60"
                                            preserveAspectRatio="none"
                                        >
                                            {gradient ? (
                                                <defs>
                                                    <linearGradient
                                                        id={bracketRightInkId}
                                                        x1="0%"
                                                        y1="0%"
                                                        x2="100%"
                                                        y2="0%"
                                                        gradientTransform={`rotate(${gradientAngle} .5 .5)`}
                                                    >
                                                        {bracketGradientColors.map(
                                                            (
                                                                stopColor,
                                                                index
                                                            ) => (
                                                                <stop
                                                                    key={`${stopColor}-${index}`}
                                                                    offset={`${
                                                                        (index /
                                                                            Math.max(
                                                                                bracketGradientColors.length -
                                                                                    1,
                                                                                1
                                                                            )) *
                                                                        100
                                                                    }%`}
                                                                    stopColor={
                                                                        stopColor
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </linearGradient>
                                                </defs>
                                            ) : null}
                                            <g transform="translate(-130 0)">
                                                <path
                                                    d={shape.strokes[1].d}
                                                    pathLength={1}
                                                    fill="none"
                                                    stroke={
                                                        gradient
                                                            ? `url(#${bracketRightInkId})`
                                                            : color ||
                                                              gradientEnd
                                                    }
                                                    strokeWidth={
                                                        shape.strokes[1].width
                                                    }
                                                    strokeLinecap="round"
                                                    data-drawn={flag}
                                                    className={
                                                        animateDecoration
                                                            ? 'marker-stroke'
                                                            : undefined
                                                    }
                                                    style={timing(
                                                        MARKER_STAGGER_MS,
                                                        1,
                                                        lineIndex
                                                    )}
                                                />
                                            </g>
                                        </svg>
                                    ) : null}
                                </span>
                            ) : shape.kind === 'bar' ? (
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        styles.decoration(),
                                        'editor-marker-decoration'
                                    )}
                                    style={
                                        variant === 'line'
                                            ? {
                                                  height: `max(${thickness}px, 0.07em)`,
                                                  borderRadius: `${radius}px`,
                                              }
                                            : undefined
                                    }
                                >
                                    <span
                                        data-drawn={flag}
                                        className={cn(
                                            styles.bar(),
                                            'editor-marker-bar',
                                            animateDecoration && 'marker-sweep'
                                        )}
                                        style={{
                                            ...timing(0, 1, lineIndex),
                                            ...decorationColor,
                                            ...(variant === 'line' && gradient
                                                ? { background: gradient }
                                                : {}),
                                            ...(variant === 'line'
                                                ? {
                                                      borderRadius: `${radius}px`,
                                                  }
                                                : {}),
                                        }}
                                    />
                                </span>
                            ) : (
                                <svg
                                    aria-hidden="true"
                                    fill="none"
                                    preserveAspectRatio="none"
                                    viewBox={renderedViewBox}
                                    data-drawn={
                                        shape.kind === 'fill' ? flag : undefined
                                    }
                                    className={cn(
                                        styles.decoration(),
                                        'editor-marker-decoration',
                                        shape.kind === 'fill' &&
                                            animateDecoration &&
                                            'marker-sweep'
                                    )}
                                    style={
                                        shape.kind === 'fill'
                                            ? {
                                                  ...timing(0, 1.1, lineIndex),
                                                  ...decorationColor,
                                              }
                                            : decorationColor
                                    }
                                >
                                    <defs>
                                        {gradient ? (
                                            <linearGradient
                                                id={renderedInkId}
                                                gradientUnits="userSpaceOnUse"
                                                x1="0"
                                                y1={renderedGradientHeight / 2}
                                                x2={renderedGradientWidth}
                                                y2={renderedGradientHeight / 2}
                                                gradientTransform={`rotate(${gradientAngle} ${
                                                    renderedGradientWidth / 2
                                                } ${
                                                    renderedGradientHeight / 2
                                                })`}
                                            >
                                                {svgGradientColors.map(
                                                    (stopColor, index) => (
                                                        <stop
                                                            key={`${stopColor}-${index}`}
                                                            offset={`${
                                                                (index /
                                                                    Math.max(
                                                                        svgGradientColors.length -
                                                                            1,
                                                                        1
                                                                    )) *
                                                                100
                                                            }%`}
                                                            stopColor={
                                                                stopColor
                                                            }
                                                        />
                                                    )
                                                )}
                                            </linearGradient>
                                        ) : null}
                                        <filter
                                            id={`${id}-hard`}
                                            x="-30%"
                                            y="-30%"
                                            width="160%"
                                            height="160%"
                                        >
                                            <feTurbulence
                                                type="fractalNoise"
                                                baseFrequency={0.045}
                                                numOctaves={2}
                                                seed={4}
                                                result="n"
                                            />
                                            <feDisplacementMap
                                                in="SourceGraphic"
                                                in2="n"
                                                scale={2.6}
                                                xChannelSelector="R"
                                                yChannelSelector="G"
                                            />
                                        </filter>
                                        <filter
                                            id={`${id}-soft`}
                                            x="-30%"
                                            y="-30%"
                                            width="160%"
                                            height="160%"
                                        >
                                            <feTurbulence
                                                type="fractalNoise"
                                                baseFrequency={0.035}
                                                numOctaves={2}
                                                seed={11}
                                                result="n"
                                            />
                                            <feDisplacementMap
                                                in="SourceGraphic"
                                                in2="n"
                                                scale={1.5}
                                                xChannelSelector="R"
                                                yChannelSelector="G"
                                            />
                                        </filter>
                                    </defs>
                                    {shape.kind === 'fill' ? (
                                        <path
                                            d={shape.d}
                                            fill={renderedInk}
                                            filter={`url(#${id}-soft)`}
                                        />
                                    ) : (
                                        renderedStrokes.map((stroke) => (
                                            <path
                                                key={stroke.d}
                                                d={stroke.d}
                                                pathLength={1}
                                                stroke={renderedInk}
                                                strokeWidth={stroke.width}
                                                strokeLinecap="round"
                                                strokeLinejoin={stroke.join}
                                                opacity={
                                                    animateDecoration
                                                        ? undefined
                                                        : stroke.opacity
                                                }
                                                filter={`url(#${id}-${stroke.rough})`}
                                                data-drawn={flag}
                                                className={
                                                    animateDecoration
                                                        ? 'marker-stroke'
                                                        : undefined
                                                }
                                                style={
                                                    {
                                                        ...timing(
                                                            stroke.second
                                                                ? MARKER_STAGGER_MS
                                                                : 0,
                                                            1,
                                                            lineIndex
                                                        ),
                                                        '--marker-opacity':
                                                            stroke.opacity ?? 1,
                                                    } as CSSProperties
                                                }
                                            />
                                        ))
                                    )}
                                </svg>
                            )}
                        </span>
                    );
                })}
            </span>
            {usesOverallShape && (!isGroupedOverall || endsOverallGroup) ? (
                <br aria-hidden="true" className="editor-marker-flow-break" />
            ) : null}
        </>
    );
}
